/*
 * Checkout Flow — <cf-quote-form> : formulaire de contact Shopify natif présenté en 3 étapes.
 * - Envoi : soumission native du formulaire (traitement serveur et protections Shopify conservés).
 * - Anti-spam invisible côté thème : champ piège, délai minimal de remplissage, blocage du double envoi.
 * - Avant l'envoi, une ligne de synthèse (service, entreprise ou nom), l'URL complète et la date sont ajoutées.
 * - Succès affiché uniquement après confirmation serveur (form.posted_successfully?).
 * - Sans JavaScript : les trois étapes sont affichées sur une seule page, validation HTML native.
 * - Aucune coordonnée n'est stockée localement ; seuls service, pays, boutique, budget et délai
 *   sont conservés en sessionStorage le temps de la visite (effacés après envoi réussi).
 */
(() => {
  const start = () => {
    const CF = window.CF;
    if (!CF || customElements.get('cf-quote-form')) return;
    const KEEP = 'cf-quote-draft';
    const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

    class CFQuoteForm extends HTMLElement {
      connectedCallback() {
        // déplacement dans l'éditeur : seul l'écouteur global est remis, l'état du formulaire est conservé
        if (!CF.firstBind(this)) { if (this.onPrefill) document.addEventListener('cf:quote-prefill', this.onPrefill); return; }
        this.form = this.querySelector('form');
        this.t = CF.i18n(this.querySelector('script[data-cf-i18n]')) || { recap: {}, countryNames: {} };
        const posted = this.querySelector('[data-cf-posted]');
        if (posted) { this.onPosted(posted); return; }
        if (!this.form) return;
        this.form.noValidate = true;
        this.steps = [...this.querySelectorAll('.cf-quote__step')];
        this.progress = [...this.querySelectorAll('.cf-quote__progress li')];
        this.btnBack = this.querySelector('[data-nav="back"]');
        this.btnNext = this.querySelector('[data-nav="next"]');
        this.btnSubmit = this.querySelector('[data-nav="submit"]');
        this.status = this.querySelector('[data-step-status]');
        this.errsum = this.querySelector('[data-errsum]');
        this.configInput = this.form.querySelector('[data-field="config"]');
        this.classList.add('is-stepped', 'is-accordion');
        this.current = 0;
        this.reached = 0;
        // étapes en accordéon : en-tête cliquable (numéro, titre, résumé du choix, « Modifier »), une seule étape ouverte
        this.heads = this.steps.map((st, k) => {
          const legend = st.querySelector('.cf-quote__legend');
          const panel = document.createElement('div');
          panel.className = 'cf-quote__panel';
          panel.id = `${this.form.id || 'cf-quote'}-panel-${k + 1}`;
          [...st.childNodes].forEach((n) => { if (n !== legend) panel.appendChild(n); });
          const head = document.createElement('button');
          head.type = 'button';
          head.className = 'cf-quote__head';
          head.setAttribute('aria-controls', panel.id);
          head.innerHTML = '<span class="cf-quote__num" aria-hidden="true"></span><span class="cf-quote__htitle"></span><span class="cf-quote__hsum"></span><span class="cf-quote__hedit"></span><svg class="cf-acc__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          head.querySelector('.cf-quote__num').textContent = String(k + 1);
          head.querySelector('.cf-quote__htitle').textContent = legend ? legend.textContent.trim() : '';
          head.querySelector('.cf-quote__hedit').textContent = this.t.edit || '';
          if (legend) { legend.classList.add('cf-visually-hidden'); legend.after(head); } else st.prepend(head);
          head.after(panel);
          st.panel = panel;
          head.addEventListener('click', () => this.headClick(k));
          return head;
        });
        this.nav = this.querySelector('.cf-quote__nav');
        // questions à choix (service, boutique, préférence de contact) : accordéons progressifs
        this.acc = CF.accordion ? CF.accordion(this.form, '[data-acc]', { edit: this.t.edit }) : null;
        this.started = false;

        this.restore();
        this.applyPrefill(CF.quote.get());
        if (this.acc) this.acc.refresh();
        this.onPrefill = (e) => this.applyPrefill(e.detail || {});
        document.addEventListener('cf:quote-prefill', this.onPrefill);

        this.btnNext.addEventListener('click', () => this.next());
        this.btnBack.addEventListener('click', () => this.go(this.current - 1, true));
        this.form.addEventListener('submit', (e) => this.submit(e));
        this.bornAt = Date.now();
        this.querySelectorAll('[data-retry]').forEach((b) => b.addEventListener('click', () => {
          const box = b.closest('[data-offline], [data-cf-server-error]');
          if (box) box.hidden = true;
          this.resetBusy();
          if (this.form.requestSubmit) this.form.requestSubmit(this.btnSubmit); else this.btnSubmit.click();
        }));
        this.form.addEventListener('input', (e) => this.onInput(e));
        this.form.addEventListener('change', (e) => this.onInput(e));
        this.form.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' && e.target.matches('input:not([type="submit"])') && this.current < this.steps.length - 1) { e.preventDefault(); this.next(); }
        });
        const rm = this.querySelector('[data-config-remove]');
        if (rm) rm.addEventListener('click', () => { this.setConfig(''); CF.quote.set({ config: '' }); });
        this.go(0, false);

        const serverError = this.querySelector('[data-cf-server-error]');
        if (serverError) {
          const invalid = this.form.querySelector('[aria-invalid="true"]');
          this.go(invalid ? this.stepOf(invalid) : 0, false);
          serverError.focus();
          CF.track('quote_error', { type: 'server' });
        }
      }

      disconnectedCallback() { if (this.onPrefill) document.removeEventListener('cf:quote-prefill', this.onPrefill); }

      focusStart() {
        const first = this.steps[this.current] && this.steps[this.current].querySelector('.cf-quote__legend');
        if (first) first.focus({ preventScroll: true });
      }

      stepOf(el) { return Math.max(0, this.steps.findIndex((s) => s.contains(el))); }

      /* ---------- navigation entre étapes ---------- */
      go(i, focus) {
        i = Math.max(0, Math.min(this.steps.length - 1, i));
        this.current = i;
        this.reached = Math.max(this.reached, i);
        this.steps.forEach((s, k) => {
          const open = k === i;
          s.classList.toggle('is-open', open);
          s.classList.toggle('is-done', k < i || (k <= this.reached && !open && this.checks(k).every(([, ok]) => ok)));
          if (s.panel) s.panel.hidden = !open;
          const h = this.heads && this.heads[k];
          if (h) {
            h.setAttribute('aria-expanded', String(open));
            h.setAttribute('aria-disabled', String(k > Math.max(this.reached, i + 1)));
            const sum = h.querySelector('.cf-quote__hsum');
            if (sum) sum.textContent = open ? '' : this.stepSummary(k);
          }
        });
        if (this.nav && this.steps[i].panel) this.steps[i].panel.appendChild(this.nav);
        this.progress.forEach((p, k) => {
          p.classList.toggle('is-current', k === i);
          p.classList.toggle('is-done', k < i);
          const done = p.querySelector('[data-done]');
          if (done) done.hidden = k >= i;
          if (k === i) p.setAttribute('aria-current', 'step'); else p.removeAttribute('aria-current');
        });
        const last = i === this.steps.length - 1;
        this.btnBack.hidden = i === 0;
        this.btnNext.hidden = last;
        this.btnSubmit.hidden = !last;
        if (this.errsum) this.errsum.hidden = true;
        if (last) this.renderRecap();
        if (this.status) this.status.textContent = String(this.t.step || '').replace('__C__', i + 1);
        if (focus) {
          const legend = this.steps[i].querySelector('.cf-quote__legend');
          if (legend) legend.focus({ preventScroll: true });
          const box = this.getBoundingClientRect();
          if (box.top < 0 || box.top > window.innerHeight * 0.4) this.scrollIntoView({ behavior: CF.motion() === 'off' ? 'auto' : 'smooth', block: 'start' });
        }
      }

      headClick(k) {
        if (k === this.current) return;
        if (k < this.current) { this.go(k, true); return; }
        if (k > Math.max(this.reached, this.current + 1)) return;
        if (!this.validate(this.current)) return;
        this.go(k, true);
      }

      stepSummary(k) {
        const f = this.form;
        const radio = (n) => { const el = f.querySelector(`input[name="${n}"]:checked`); return el ? el.nextElementSibling.textContent.trim() : ''; };
        const sel = (n) => { const el = f.querySelector(`select[name="${n}"]`); return el && el.value && el.selectedIndex > 0 ? el.options[el.selectedIndex].textContent.trim() : ''; };
        const val = (n) => { const el = f.querySelector(`[name="${n}"]`); return el ? el.value.trim() : ''; };
        let parts = [];
        if (k === 0) parts = [radio('contact[Service]'), sel('contact[Country]')];
        else if (k === 1) { const b = val('contact[body]'); parts = [b.length > 48 ? b.slice(0, 46) + '…' : b, sel('contact[Budget]')]; }
        else parts = [val('contact[name]'), val('contact[email]')];
        return parts.filter(Boolean).join(' · ');
      }

      next() {
        if (!this.validate(this.current)) return;
        CF.track('form_step', { step: this.current + 2 });
        this.go(this.current + 1, true);
      }

      /* ---------- validation (messages sous chaque champ, focus sur le premier champ en erreur) ---------- */
      fieldError(wrapName, show) {
        const wrap = this.form.querySelector(`[data-field-wrap="${wrapName}"]`);
        if (!wrap) return;
        const err = wrap.querySelector('.cf-field__error');
        if (err) err.hidden = !show;
        if (show && wrap._acc && this.acc) this.acc.open(wrap, false);
        wrap.classList.toggle('has-error', show);
        wrap.querySelectorAll('input, select, textarea').forEach((el) => {
          if (el.type === 'radio') return;
          if (show) el.setAttribute('aria-invalid', 'true'); else el.removeAttribute('aria-invalid');
        });
        const group = wrap.querySelector('[role="radiogroup"]');
        if (group) { if (show) group.setAttribute('aria-invalid', 'true'); else group.removeAttribute('aria-invalid'); }
      }

      checks(step) {
        const f = this.form;
        const val = (sel) => { const el = f.querySelector(sel); return el ? el.value.trim() : ''; };
        const svc = f.querySelector('input[name="contact[Service]"]:checked');
        const svcKey = svc ? svc.getAttribute('data-key') : '';
        const store = f.querySelector('input[name="contact[Store]"]:checked');
        if (step === 0) {
          const url = val('input[name="contact[Store URL]"]');
          let urlOk = true;
          if (url) { try { const u = new URL(/^https?:\/\//i.test(url) ? url : 'https://' + url); urlOk = /\./.test(u.hostname); } catch (e) { urlOk = false; } }
          return [
            ['service', !!svc],
            ['country', svcKey !== 'sumup' || !!val('select[name="contact[Country]"]')],
            ['url', urlOk || (store && store.getAttribute('data-key') === 'store_none')],
          ];
        }
        if (step === 1) return [['body', !!val('textarea[name="contact[body]"]')]];
        const email = val('input[name="contact[email]"]');
        return [
          ['name', !!val('input[name="contact[name]"]')],
          ['email', /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)],
        ];
      }

      validate(step) {
        let firstBad = null;
        let bad = 0;
        this.checks(step).forEach(([name, ok]) => {
          this.fieldError(name, !ok);
          if (!ok) { bad += 1; if (!firstBad) firstBad = name; }
        });
        if (firstBad) {
          const wrap = this.form.querySelector(`[data-field-wrap="${firstBad}"]`);
          const target = wrap && (wrap.querySelector('input:not([type="radio"]), select, textarea') || wrap.querySelector('input'));
          if (target) {
            target.focus({ preventScroll: true });
            wrap.scrollIntoView({ behavior: CF.motion() === 'off' ? 'auto' : 'smooth', block: 'center' });
          }
          // un seul champ en erreur : son message (lu au focus via aria-describedby) suffit ; plusieurs : résumé en plus
          if (this.errsum) this.errsum.hidden = bad < 2;
          CF.track('form_error', { step: step + 1, field: firstBad });
          return false;
        }
        if (this.errsum) this.errsum.hidden = true;
        return true;
      }

      onInput(e) {
        if (!this.started) { this.started = true; CF.track('form_start', {}, true); }
        const wrap = e.target.closest('[data-field-wrap]');
        if (wrap && wrap.classList.contains('has-error')) {
          const name = wrap.getAttribute('data-field-wrap');
          const res = this.checks(this.stepOf(wrap)).find(([n]) => n === name);
          if (res && res[1]) this.fieldError(name, false);
          if (this.errsum && !this.form.querySelector('.has-error')) this.errsum.hidden = true;
        }
        if (e.target.name === 'contact[Service]') {
          const key = e.target.getAttribute('data-key');
          this.toggleCountryRequired(key);
          if (e.type === 'change') CF.track('service_select', { service: key });
        }
        if (e.target.name === 'contact[Store]') this.toggleUrl();
        this.save();
      }

      toggleCountryRequired(key) {
        const star = this.querySelector('[data-req-country]');
        if (star) star.hidden = key !== 'sumup';
      }

      toggleUrl() {
        const store = this.form.querySelector('input[name="contact[Store]"]:checked');
        const wrap = this.form.querySelector('[data-field-wrap="url"]');
        const none = store && store.getAttribute('data-key') === 'store_none';
        if (wrap) wrap.hidden = !!none;
        if (none) this.fieldError('url', false);
      }

      /* ---------- préremplissage (service, détail, pays, configurateur) ---------- */
      applyPrefill(p) {
        if (!p) return;
        if (p.service) {
          const r = this.form.querySelector(`input[name="contact[Service]"][data-key="${p.service}"]`);
          if (r) { r.checked = true; this.toggleCountryRequired(p.service); this.fieldError('service', false); }
        }
        if (p.country) this.selectCountry(p.country);
        if (this.acc) {
          const w = this.form.querySelector('[data-field-wrap="service"]');
          if (w && w._acc) { this.acc.sync(w); if (w.classList.contains('is-answered') && w.classList.contains('is-open')) { this.acc.close(w); this.acc.next(w); } }
        }
        if (this.heads) this.go(this.current, false);
        if (p.config !== undefined) this.setConfig(p.config || '');
        if (p.detail) {
          const body = this.form.querySelector('textarea[name="contact[body]"]');
          const known = { paypal: 'PayPal', card: 'Card payments', provider: 'Own provider', country_not_listed: 'Country not on SumUp list' };
          if (this.configInput && known[p.detail] && !this.configInput.value) this.configInput.value = 'Detail: ' + known[p.detail];
          if (body && !body.value && p.detail === 'country_not_listed') body.focus({ preventScroll: true });
        }
        this.save();
      }

      selectCountry(code) {
        const sel = this.form.querySelector('select[name="contact[Country]"]');
        if (!sel) return;
        const names = [norm(this.t.countryNames[code] || ''), norm(code)];
        let found = [...sel.options].find((o) => o.getAttribute('data-code') === code);
        if (!found) found = [...sel.options].find((o) => names.includes(norm(o.textContent)) || names.includes(norm(o.value)));
        if (found) { sel.value = found.value; this.fieldError('country', false); }
      }

      setConfig(text) {
        if (this.configInput) this.configInput.value = text;
        const chip = this.querySelector('[data-config-chip]');
        if (chip) { chip.hidden = !text; const tx = chip.querySelector('[data-config-text]'); if (tx) tx.textContent = text; }
      }

      /* ---------- récapitulatif (étape 3) ---------- */
      renderRecap() {
        const box = this.querySelector('[data-recap]');
        const list = this.querySelector('[data-recap-list]');
        if (!box || !list) return;
        const f = this.form, T = this.t.recap || {};
        const radioLabel = (name) => { const el = f.querySelector(`input[name="${name}"]:checked`); return el ? el.nextElementSibling.textContent.trim() : ''; };
        const selectLabel = (name) => { const el = f.querySelector(`select[name="${name}"]`); return el && el.value ? el.options[el.selectedIndex].textContent.trim() : ''; };
        const rows = [
          [T.service, radioLabel('contact[Service]'), 0],
          [T.country, selectLabel('contact[Country]'), 0],
          [T.store, radioLabel('contact[Store]'), 0],
          [T.budget, selectLabel('contact[Budget]'), 1],
          [T.timeline, selectLabel('contact[Timeline]'), 1],
          [T.config, this.configInput ? this.configInput.value : '', 1],
        ].filter((r) => r[1]);
        list.innerHTML = '';
        rows.forEach(([k, v, step]) => {
          const row = document.createElement('div');
          row.className = 'cf-quote__recap-row';
          const dt = document.createElement('dt'); dt.textContent = k;
          const dd = document.createElement('dd'); dd.textContent = v;
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cf-quote__edit';
          btn.textContent = this.t.edit || 'Edit';
          btn.setAttribute('aria-label', `${this.t.edit || 'Edit'}: ${k}`);
          btn.addEventListener('click', () => this.go(step, true));
          dd.appendChild(btn);
          row.appendChild(dt); row.appendChild(dd);
          list.appendChild(row);
        });
        box.hidden = !rows.length;
      }

      /* ---------- conservation locale non personnelle ---------- */
      save() {
        const f = this.form;
        const pick = (sel) => { const el = f.querySelector(sel); return el ? el.value : ''; };
        const radio = (name) => { const el = f.querySelector(`input[name="${name}"]:checked`); return el ? el.value : ''; };
        const draft = { service: radio('contact[Service]'), country: pick('select[name="contact[Country]"]'), store: radio('contact[Store]'), url: pick('input[name="contact[Store URL]"]'), budget: pick('select[name="contact[Budget]"]'), timeline: pick('select[name="contact[Timeline]"]'), config: this.configInput ? this.configInput.value : '' };
        try { sessionStorage.setItem(KEEP, JSON.stringify(draft)); } catch (e) { /* noop */ }
      }

      restore() {
        let d = null;
        try { d = JSON.parse(sessionStorage.getItem(KEEP) || 'null'); } catch (e) { d = null; }
        if (!d) return;
        const f = this.form;
        const setRadio = (name, v) => { if (!v) return; const el = [...f.querySelectorAll(`input[name="${name}"]`)].find((i) => i.value === v); if (el) el.checked = true; };
        const setVal = (sel, v) => { const el = f.querySelector(sel); if (el && v) el.value = v; };
        setRadio('contact[Service]', d.service);
        setRadio('contact[Store]', d.store);
        setVal('select[name="contact[Country]"]', d.country);
        setVal('input[name="contact[Store URL]"]', d.url);
        setVal('select[name="contact[Budget]"]', d.budget);
        setVal('select[name="contact[Timeline]"]', d.timeline);
        if (d.config) this.setConfig(d.config);
        const svc = f.querySelector('input[name="contact[Service]"]:checked');
        if (svc) this.toggleCountryRequired(svc.getAttribute('data-key'));
        this.toggleUrl();
      }

      /* ---------- envoi : natif, protégé contre le double envoi ---------- */
      submit(e) {
        if (this.sending) { e.preventDefault(); return; }
        for (let i = 0; i < this.steps.length; i++) {
          if (!this.validate(i)) { e.preventDefault(); if (i !== this.current) { this.go(i, false); this.validate(i); } return; }
        }
        if (navigator.onLine === false) {
          e.preventDefault();
          const off = this.querySelector('[data-offline]');
          if (off) { off.hidden = false; off.focus && off.setAttribute('tabindex', '-1'); off.focus(); }
          CF.track('quote_error', { type: 'offline' });
          return;
        }
        // anti-spam invisible : champ piège rempli ou envoi en moins de 3 s = robot. Rien n'est envoyé,
        // et le message affiché dit que la demande n'a pas été reçue (jamais de faux succès).
        const hp = this.form.querySelector('[data-hp]');
        if ((hp && hp.value) || Date.now() - (this.bornAt || 0) < 3000) {
          e.preventDefault();
          const off = this.querySelector('[data-offline]');
          if (off) { off.hidden = false; off.setAttribute('tabindex', '-1'); off.focus(); }
          CF.track('quote_error', { type: 'blocked' });
          return;
        }
        const url = this.form.querySelector('input[name="contact[Store URL]"]');
        if (url && url.value && !/^https?:\/\//i.test(url.value.trim())) url.value = 'https://' + url.value.trim();
        this.fillMeta();
        this.sending = true;
        this.btnSubmit.setAttribute('aria-disabled', 'true');
        this.btnSubmit.classList.add('is-busy');
        this.btnBack.disabled = true;
        CF.track('quote_submit', {});
        try { sessionStorage.setItem('cf-quote-submitted', String(Date.now())); } catch (err) { /* noop */ }
        window.addEventListener('pageshow', (ev) => { if (ev.persisted) this.resetBusy(); }, { once: true });
      }

      /* synthèse lisible en tête de l'e-mail reçu : valeurs anglaises stables, quelle que soit la langue du visiteur */
      fillMeta() {
        const f = this.form;
        const val = (sel) => { const el = f.querySelector(sel); return el ? String(el.value || '').trim() : ''; };
        const svc = val('input[name="contact[Service]"]:checked') || 'Quote';
        const who = val('input[name="contact[Company]"]') || val('input[name="contact[name]"]');
        const req = f.querySelector('[data-field="request"]');
        if (req) req.value = `Nouvelle demande de devis Checkout Flow — ${svc}${who ? ' — ' + who : ''}`;
        const date = f.querySelector('[data-field="date"]');
        if (date) {
          const d = new Date();
          let tz = '';
          try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (err) { /* noop */ }
          date.value = `${d.toISOString().replace('T', ' ').slice(0, 16)} UTC${tz ? ' (visitor time zone: ' + tz + ')' : ''}`;
        }
        const page = f.querySelector('input[name="contact[Page]"]');
        if (page) { try { const u = new URL(window.location.href); u.hash = ''; page.value = u.toString(); } catch (err) { /* noop */ } }
      }

      resetBusy() {
        this.sending = false;
        this.btnSubmit.removeAttribute('aria-disabled');
        this.btnSubmit.classList.remove('is-busy');
        this.btnBack.disabled = false;
      }

      /* ---------- demande confirmée par Shopify ---------- */
      onPosted(el) {
        let submittedAt = 0;
        try { submittedAt = parseInt(sessionStorage.getItem('cf-quote-submitted') || '0', 10); } catch (e) { /* noop */ }
        if (submittedAt) {
          const service = (() => { try { return JSON.parse(sessionStorage.getItem(KEEP) || '{}').service || ''; } catch (e) { return ''; } })();
          CF.track('quote_confirmed', { service });
          try { sessionStorage.removeItem('cf-quote-submitted'); sessionStorage.removeItem(KEEP); } catch (e) { /* noop */ }
          CF.quote.clear();
        }
        try {
          const u = new URL(window.location.href);
          if (u.searchParams.has('contact_posted')) { u.searchParams.delete('contact_posted'); history.replaceState(history.state, '', u.toString()); }
        } catch (e) { /* noop */ }
        el.focus({ preventScroll: false });
      }
    }
    customElements.define('cf-quote-form', CFQuoteForm);
  };
  if (window.CF) start(); else document.addEventListener('cf:ready', start, { once: true });
})();
